import { useContext, useState } from 'react';
import { Icon as BlueIcon } from '@blueprintjs/core';
import { AssetContext } from '@pages/Asset/Asset.query';
import { GlobalMenu } from '@components';
import { EquipmentFieldConfigurationTypeOptions } from '@models/FieldConfiguration.model';
import { updateOrderFieldConfiguration } from '@services/queries/fieldsConfiguration';
import { useMutation, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { checkDevice } from '@store/redux/features/mobile/mobileSelectors';
import {
  Field,
  FieldHead,
  FieldName,
  FieldNameText,
  FieldPinned,
} from './Data.styles';

interface ILabelMenu {
  label: string;
  children: JSX.Element;
  fieldName?: EquipmentFieldConfigurationTypeOptions;
  pinned?: boolean;
  customFieldId?: string;
}
export const LabelMenu = ({
  label,
  pinned,
  children,
  fieldName,
  customFieldId,
}: ILabelMenu) => {
  const [showMenu, setShowMenu] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const { configQuery } = useContext(AssetContext);

  const config = configQuery?.data;

  const queryClient = useQueryClient();

  const mutationUpdateOrderFieldConfiguration = useMutation(
    updateOrderFieldConfiguration,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['fieldsConfiguration', 'productHeader']);
      },
    },
  );

  const { isDesktop } = useSelector(checkDevice());
  const onMouseOver = () => {
    if (isDesktop) setShowMenu(true);
  };
  const onMouseLeave = () => {
    if (isDesktop) setShowMenu(false);
  };
  return (
    <Field
      onMouseOver={onMouseOver}
      onMouseLeave={onMouseLeave}
      data-testid="ieid-field-label"
    >
      <FieldHead>
        <FieldName>
          <FieldNameText>{label}</FieldNameText>
          {pinned && (
            <FieldPinned>
              <BlueIcon icon="pin" color="#8F95B2" />
            </FieldPinned>
          )}
        </FieldName>

        {(showMenu || isOpen) && (
          <GlobalMenu
            align="end"
            title="Options"
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            itemList={[
              {
                label: `${pinned ? 'Unpin' : 'Pin'} ${label}`,
                iconName: 'pin',
                onClick() {
                  if (config) {
                    if (pinned) {
                      const configNoDragged = config
                        .sort((a, b) => a.order - b.order)
                        .filter(item =>
                          customFieldId
                            ? item.customField?.id !== customFieldId
                            : item.fieldName !== fieldName,
                        )
                        .map(item =>
                          item.customField
                            ? {
                                customFieldId: item.customField.id,
                                order: item.order,
                              }
                            : {
                                fieldName: item.fieldName,
                                order: item.order,
                              },
                        );

                      const updatedOrders = configNoDragged.map(
                        (item, idx) => ({
                          ...item,
                          order: idx,
                        }),
                      );

                      mutationUpdateOrderFieldConfiguration.mutate({
                        location: 'productHeader',
                        data: { updatedOrders },
                      });
                    } else {
                      const updatedOrders = config.map(
                        (fieldConfiguration, index) => {
                          if (fieldConfiguration.customField) {
                            return {
                              order: index,
                              customFieldId: fieldConfiguration.customField.id,
                            };
                          }
                          return {
                            order: index,
                            fieldName: fieldConfiguration.fieldName,
                          };
                        },
                      );

                      updatedOrders.push(
                        customFieldId
                          ? {
                              order: updatedOrders.length,
                              customFieldId,
                            }
                          : {
                              order: updatedOrders.length,
                              fieldName,
                            },
                      );

                      mutationUpdateOrderFieldConfiguration.mutate({
                        location: 'productHeader',
                        data: { updatedOrders },
                      });
                    }
                  }
                },
              },
            ]}
          />
        )}
      </FieldHead>

      {children}
    </Field>
  );
};
